import { useEffect } from 'react';

// Petit helper pour mettre à jour des balises meta au montage d'une page
// et les restaurer au démontage. Pas de dépendance externe (react-helmet non requis).
//
// Usage:
//   useMetaTags({
//     title: 'Mon article — Code Voiture',
//     description: 'Résumé de l'article',
//     ogImage: 'https://...',
//     ogUrl: 'https://codevoiture.fr/magazine/mon-article',
//     ogType: 'article',
//   });

function setMeta(selector, attrName, attrValue, content) {
  let el = document.head.querySelector(selector);
  const prev = el ? el.getAttribute('content') : null;
  const wasCreated = !el;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return { el, prev, wasCreated };
}

export default function useMetaTags(opts) {
  useEffect(() => {
    if (!opts) return;
    const {
      title,
      description,
      ogImage,
      ogUrl,
      ogType = 'article',
      twitterCard = 'summary_large_image',
    } = opts;

    const restore = [];
    const prevTitle = document.title;
    if (title) document.title = title;

    if (description) {
      restore.push(setMeta('meta[name="description"]', 'name', 'description', description));
      restore.push(setMeta('meta[property="og:description"]', 'property', 'og:description', description));
      restore.push(setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description));
    }
    if (title) {
      restore.push(setMeta('meta[property="og:title"]', 'property', 'og:title', title));
      restore.push(setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title));
    }
    if (ogImage) {
      restore.push(setMeta('meta[property="og:image"]', 'property', 'og:image', ogImage));
      restore.push(setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImage));
    }
    if (ogUrl) {
      restore.push(setMeta('meta[property="og:url"]', 'property', 'og:url', ogUrl));
    }
    if (ogType) {
      restore.push(setMeta('meta[property="og:type"]', 'property', 'og:type', ogType));
    }
    if (twitterCard) {
      restore.push(setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', twitterCard));
    }

    return () => {
      document.title = prevTitle;
      restore.forEach(({ el, prev, wasCreated }) => {
        if (wasCreated) {
          try { el.remove(); } catch { /* noop */ }
        } else if (prev !== null) {
          el.setAttribute('content', prev);
        }
      });
    };
    // opts is a plain object — dependencies handled inside via primitive fields
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts?.title, opts?.description, opts?.ogImage, opts?.ogUrl, opts?.ogType, opts?.twitterCard]);
}
